import { inject, Injector } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { StorageService } from '../services/storage.service';
import { LocationStore } from '../store/location.store';
import { SKIP_AUTH_REDIRECT_HEADER, SKIP_LOCATION_ID_HEADER } from '../api/api.models';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  // Lazily resolved (never a constructor-time inject()) — AuthService's own constructor calls
  // restoreSession() -> loadMasterData() -> fetchPermissions(), which fires an HTTP request
  // through this very interceptor. Injecting AuthService eagerly here re-enters its provider
  // while it is still being constructed (NG0200) on every reload, breaking that very first
  // request. Deferring resolution to the point of actual use (inside the 401 branch below,
  // reached only once the async response has already unwound past construction) avoids it —
  // same pattern as auth.store.ts's own ClinicProfileStore/injector usage.
  const injector = inject(Injector);
  const router = inject(Router);
  const locationStore = inject(LocationStore);
  const isOwnApiRequest = req.url.startsWith(environment.endPoint);

  // Third-party APIs (e.g. ipinfo.io) must never receive our session headers — ipinfo reads
  // `Authorization: Bearer …` as its own API token and rejects the request with 403.
  const token = isOwnApiRequest ? storage.getToken() : null;

  // Native (Capacitor) app: the WebView's real origin is always `localhost`/`capacitor://localhost`,
  // which the backend can't resolve to any company — use the configured tenant domain instead.
  // Web: the page's real host — the backend resolves the tenant from it, so a hardcoded
  // `localhost:4200` would only work locally and break every deployed domain.
  const requestUrl = Capacitor.isNativePlatform() ? environment.domain : window.location.host;
  const headers: Record<string, string> = isOwnApiRequest ? { RequestUrl: requestUrl } : {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Opted out by the caller (ApiRequestOptions.skipLocationId) — e.g. DoctorStation/GetWorklist,
  // which is scoped by doctor id rather than the user's currently selected location.
  const skipLocationId = req.headers.has(SKIP_LOCATION_ID_HEADER);
  const needsLocationId = isOwnApiRequest && !skipLocationId && !req.params.has('locationId');

  // Opted out by the caller (ApiRequestOptions.skipAuthRedirect) — strip the marker so it
  // never reaches the backend, and leave the 401/403 handling below to the caller.
  const skipAuthRedirect = req.headers.has(SKIP_AUTH_REDIRECT_HEADER);

  let outgoingHeaders = req.headers;
  if (skipAuthRedirect) outgoingHeaders = outgoingHeaders.delete(SKIP_AUTH_REDIRECT_HEADER);
  if (skipLocationId) outgoingHeaders = outgoingHeaders.delete(SKIP_LOCATION_ID_HEADER);

  const authReq = req.clone({
    setHeaders: headers,
    headers: outgoingHeaders,
    params: needsLocationId ? req.params.set('locationId', locationStore.locationId()) : req.params,
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only our own API's 401/403 mean "session invalid" / "access denied" — a third-party
      // failure must never log the user out or bounce them to the dashboard.
      if (skipAuthRedirect || !isOwnApiRequest) return throwError(() => error);

      if (error.status === 401) {
        // Token expired or invalid — full session reset (storage, auth store, every other
        // feature SignalStore, and the one-shot master-data guard) so the next sign-in —
        // possibly a different user — always re-fetches permissions/subscriptions instead of
        // silently reusing this session's stale data. Must go through AuthService.logout()
        // rather than a partial manual reset here; see its own doc comment.
        injector.get(AuthService).logout();
        return throwError(() => error);
      }

      if (error.status === 403) {
        router.navigate(['/dashboard']);
        return throwError(() => error);
      }

      return throwError(() => error);
    }),
  );
};
